<template>
    <div>
        <div class="tw-fixed tw-bottom-0 tw-w-full tw-z-50 tw-bg-gray-100">
            <div class="tw-h-2 tw-bg-gray-200">
                <div
                    class="tw-h-2 tw-bg-indigo-500 tw-transition-all tw-duration-300"
                    :style="{
                        width: `${Math.ceil(
                            (activeInteractiveStepNumber * 100) / lastInteractiveStepNumber,
                        )}%`,
                    }"
                ></div>
            </div>

            <div class="tw-flex tw-justify-center tw-items-center tw-p-4">
                <div class="tw-w-1/2 tw-text-left">
                    <l-base-button
                        v-if="activeInteractiveStepNumber !== 1"
                        :button-types="['secondary', 'raised', 'inline']"
                        data-test-id="survey-navigate-prev"
                        @click.native="onNavigateToNextStep(-1)"
                    >
                        <span><i class="fal fa-chevron-left tw-mr-2"/></span>
                        <span>{{ $t("surveys.participate.btnPrevious") }}</span>
                    </l-base-button>

                    <div v-if="activeInteractiveStepNumber !== 1" class="tw-mt-1 tw-leading-none">
                        <l-keyboard-hint-text :keys="['leftArrow']">
                            <span slot="prefix">{{ $t("surveys.participate.keyPress") }} </span>
                        </l-keyboard-hint-text>
                    </div>
                </div>

                <div class="tw-flex-shrink-0 tw-text-center tw-select-none">
                    <div class="tw-text-sm tw-text-gray-600 tw-text-center tw-tabular-nums">
                        <span class="tw-text-gray-400 tw-block">
                            {{ $t("surveys.participate.progressHeading") }}
                            <span class="tw-hidden md:tw-inline-block"
                                >({{ $t("surveys.participate.autoSave") }})</span
                            >
                            <i
                                v-b-popover.hover.top="$t('surveys.participate.answersCachedHint')"
                                class="fal fa-info-circle tw-cursor-help md:tw-ml-1"
                            />
                        </span>
                        {{ activeInteractiveStepNumber }} / {{ lastInteractiveStepNumber }}
                    </div>
                </div>

                <div class="tw-w-1/2 tw-text-right">
                    <div
                        v-if="activeInteractiveStepNumber >= lastInteractiveStepNumber"
                        v-b-popover.hover.top="
                            !canConfirmAnswer
                                ? $t('surveys.participate.questionRequiresResponse')
                                : isPreview
                                ? $t('surveys.participate.previewHint')
                                : ''
                        "
                        class="tw-inline-block"
                    >
                        <l-base-button
                            :disabled="!canConfirmAnswer || isLoading || isPreview"
                            :button-types="['primary', 'raised', 'inline']"
                            data-test-id="survey-navigate-submit"
                            @click.native="onSubmit()"
                        >
                            <span>{{ $t("surveys.participate.btnSubmit") }}</span>
                            <span><i class="fal fa-check tw-ml-2"/></span>
                        </l-base-button>
                    </div>

                    <l-base-button
                        v-else-if="canSkipQuestion && !hasGivenAnswer"
                        :button-types="['secondary', 'raised', 'inline']"
                        data-test-id="survey-navigate-skip"
                        @click.native="onNavigateToNextStep()"
                    >
                        <span>{{ $t("surveys.participate.btnSkip") }}</span>
                        <span><i class="fal fa-chevron-right tw-ml-2"/></span>
                    </l-base-button>

                    <div
                        v-else
                        v-b-popover.hover.top="
                            !canConfirmAnswer
                                ? $t('surveys.participate.questionRequiresResponse')
                                : ''
                        "
                        class="tw-inline-block"
                    >
                        <l-base-button
                            :disabled="!canConfirmAnswer"
                            :button-types="['primary', 'raised', 'inline']"
                            data-test-id="survey-navigate-confirm"
                            @click.native="onNavigateToNextStep()"
                        >
                            <span>{{ $t("surveys.participate.btnConfirm") }}</span>
                            <span><i class="fal fa-chevron-right tw-ml-2"/></span>
                        </l-base-button>
                    </div>

                    <div
                        v-if="
                            activeInteractiveStepNumber < lastInteractiveStepNumber &&
                                (canConfirmAnswer || canSkipQuestion)
                        "
                        class="tw-mt-1 tw-leading-none"
                    >
                        <l-keyboard-hint-text :keys="['enter']">
                            <span slot="prefix">{{ $t("surveys.participate.keyPress") }} </span>
                        </l-keyboard-hint-text>
                    </div>
                </div>
            </div>

            <div
                v-if="showAlmostDoneHint"
                data-test-id="survey-progress-hint"
                class="tw-animate-bounce tw-transition-opacity tw-duration-500 tw-absolute tw-bottom-full tw-right-4 tw-p-2 tw-rounded tw-bg-white tw-text-center tw-mb-4 tw-whitespace-nowrap"
                :class="{
                    'tw-opacity-0': !hasEnabledBounceAnimationOnHint,
                    'tw-opacity-90': hasEnabledBounceAnimationOnHint,
                }"
            >
                <template v-if="halfWayDone">
                    {{ $t("surveys.participate.halfWayThroughHint") }}
                    <span class="tw-ml-1" role="img" aria-label="smiley">&#128513;</span>
                </template>
                <template v-else-if="prettyMuchDone">
                    {{ $t("surveys.participate.almostDoneHint") }}
                    <span class="tw-ml-1" role="img" aria-label="party">&#127881;</span>
                </template>
            </div>
        </div>

        <l-text-alert
            v-if="errorMessage"
            type="danger"
            :text="errorMessage"
            class="tw-absolute tw-right-2 tw-top-14"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LBaseButton from "@/components/LBaseButton";
import LTextAlert from "@/components/LTextAlert";
import LKeyboardHintText from "@/components/LKeyboardHintText";

export default {
    components: {
        LBaseButton,
        LTextAlert,
        LKeyboardHintText,
    },
    data() {
        return {
            errorMessage: null,
            isLoading: false,
            hasEnabledBounceAnimationOnHint: false,
        };
    },
    computed: {
        ...mapGetters({
            activeInteractiveStepNumber: "surveys/answerSession/activeInteractiveStepNumber",
            lastInteractiveStepNumber: "surveys/answerSession/lastInteractiveStepNumber",
            activeStep: "surveys/answerSession/activeStep",
            userAnswers: "surveys/answerSession/userAnswers",
            interactiveSteps: "surveys/answerSession/interactiveSteps",
            survey: "surveys/answerSession/survey",
            surveyNotification: "surveys/answerSession/surveyNotification",
            canSkipQuestion: "surveys/answerSession/canSkipQuestion",
            canConfirmAnswer: "surveys/answerSession/canConfirmAnswer",
            hasGivenAnswer: "surveys/answerSession/hasGivenAnswer",
            isMobileOrTablet: "isMobileOrTablet",
        }),
        isPreview() {
            return this.$route.params?.notificationId === "preview";
        },
        prettyMuchDone() {
            return (
                this.activeInteractiveStepNumber === Math.ceil(this.lastInteractiveStepNumber * 0.8)
            );
        },
        halfWayDone() {
            return (
                this.activeInteractiveStepNumber === Math.ceil(this.lastInteractiveStepNumber / 2)
            );
        },
        /**
         * We show a hint when:
         * - we have more than 8 questions & are half-way through
         * - we have more than 16 questions and are 4/5 through
         */
        showAlmostDoneHint() {
            if (this.interactiveSteps.length < 8) return false;
            const isEnabled =
                this.halfWayDone || (this.interactiveSteps.length >= 16 && this.prettyMuchDone);

            if (isEnabled) this.triggerHintTimer();

            return isEnabled;
        },
    },
    methods: {
        triggerHintTimer() {
            this.hasEnabledBounceAnimationOnHint = true;

            setTimeout(() => {
                this.hasEnabledBounceAnimationOnHint = false;
            }, 4.5 * 1000);
        },
        onNavigateToNextStep(direction = 1) {
            // check required because of easy html manipulation
            if (direction === 1 && !this.canConfirmAnswer && !this.canSkipQuestion) return;

            this.$store.dispatch("surveys/answerSession/navigateToNextStep", {
                payload: { direction },
            });
        },
        async onSubmit() {
            this.isLoading = true;
            this.errorMessage = null;

            // prepare response payload for backend
            const feedbackSignals = [];
            for (const question of this.interactiveSteps) {
                // we define "-100" as default if no response is given
                const score = this.userAnswers?.[question.skill._id]?.score ?? -100;
                const comment = this.userAnswers?.[question.skill._id]?.comment ?? "";
                const selectedOptions =
                    this.userAnswers?.[question.skill._id]?.selectedOptions ?? [];

                feedbackSignals.push({
                    // relevant for answer
                    content: comment,
                    amount: score,
                    ...(question.skill.answerType === "multipleChoice"
                        ? { answers: selectedOptions }
                        : {}),
                    // other
                    weight: question.skill.weight,
                    skillAnswerType: question.skill.answerType,
                    label: question.skill.label,
                    status: question.skill.status,
                    skill: question.skill._id,
                    skillname: question.skill.name,
                    skilltype: question.skill.type,
                    skilldescription:
                        question.skill.descriptionCustomRoles?.[question.skill.customRole]?.main ||
                        question.skill.description,
                    skillcategory: question.skill.category,
                    progress: question.skill.progress,
                    ratingRequirement: question.skill.ratingRequirement,
                    commentRequirement: question.skill.commentRequirement,
                    applicableToRole: question.skill.applicableToRole,
                    inputRequired: question.skill.inputRequired,
                    role: question.skill.role || "all",
                    customRole: question.skill.customRole,
                    descriptionCustomRoles: question.skill.descriptionCustomRoles,
                    multipleChoiceOptions: question.skill.multipleChoiceOptions,
                    allowMultipleAnswers: question.skill.allowMultipleAnswers,
                    type: "survey",
                    scaleType: question.scaleType,
                });
            }

            const payload = {
                feedbackSignals,
                survey: this.$route.params.surveyId,
                surveyNotification: this.$route.params.notificationId,
                surveyPeriodStart: this.surveyNotification?.parameters?.surveyPeriodStart,
                surveyPeriodEnd: this.surveyNotification?.parameters?.surveyPeriodEnd,
                sender: { _id: this.surveyNotification?.receiver?.user?._id },
                team: this.survey?.team?._id,
                type: "survey",
            };

            try {
                await this.$store.dispatch("surveys/answerSession/submitAnswers", { payload });
                this.$emit("submitted");
            } catch {
                this.errorMessage = this.$t("surveys.participate.submitError");
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>
